import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { applySeo } from "../lib/seo";
import { ArrowLeft } from "lucide-react";

// What the search results show under the title of each document, and the one address of the page
const SEO = {
  "/privacy": {
    path: "/privacy",
    it: "Informativa sulla privacy del sito gozzoguide.com: quali dati tratta il sito e come esercitare i tuoi diritti.",
    en: "Privacy policy of the gozzoguide.com website: what data the website processes and how to exercise your rights.",
  },
  "/terms": {
    path: "/terms",
    it: "Termini e condizioni d'uso del sito gozzoguide.com.",
    en: "Terms and conditions of use of the gozzoguide.com website.",
  },
  "/cookie": {
    path: "/cookie",
    it: "Cookie policy del sito gozzoguide.com: il sito non imposta cookie e ricorda soltanto la lingua scelta.",
    en: "Cookie policy of the gozzoguide.com website: the site sets no cookies and only remembers the language you chose.",
  },
  "/cookies": { path: "/cookie" },
  "/app-privacy": {
    path: "/app-privacy",
    it: "Informativa sulla privacy dell'app GozzoGuide: dati dell'account, posizione, avvisi di vicinanza e cancellazione dell'account.",
    en: "Privacy policy of the GozzoGuide app: account data, location, proximity alerts and account deletion.",
  },
  "/app-terms": {
    path: "/app-terms",
    it: "Termini e condizioni d'uso dell'app GozzoGuide.",
    en: "Terms and conditions of use of the GozzoGuide app.",
  },
  "/app-cookie": {
    path: "/app-cookie",
    it: "Cookie policy dell'app GozzoGuide: archiviazione sul dispositivo e assenza di tracciamento.",
    en: "Cookie policy of the GozzoGuide app: storage on the device and no tracking.",
  },
};

const LABELS = {
  it: { back: "Torna alla Home", lastUpdated: "Ultimo aggiornamento" },
  en: { back: "Back to Home", lastUpdated: "Last updated" },
};

export const LegalLayout = ({
  title,
  lastUpdate,
  children,
  language,
  onLanguageChange,
}) => {
  const isBilingual = typeof onLanguageChange === "function";
  const lang = language === "en" ? "en" : "it";
  const labels = LABELS[lang];
  const { pathname } = useLocation();

  // Its own description and one canonical address for the page (/cookies is the same page as /cookie)
  useEffect(() => {
    const entry = SEO[pathname];
    if (!entry) return undefined;
    const base = SEO[entry.path];
    return applySeo({ description: base[lang], path: entry.path });
  }, [pathname, lang]);

  // Each legal page has its own title in the tab and in the search results
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = `${title} — GozzoGuide`;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  return (
    <div className="min-h-screen bg-[#09101F]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-nav py-4 px-6 md:px-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-body text-sm">{labels.back}</span>
          </Link>
          <Link to="/" data-testid="header-logo">
            <img
              src="/images/logo-192.png"
              alt="GozzoGuide Logo"
              className="h-8 w-auto rounded-lg"
            />
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 md:py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Language toggle */}
          {isBilingual && (
            <div
              className="flex justify-end mb-6"
              data-testid="language-toggle"
            >
              <div className="inline-flex items-center gap-0 rounded-full border border-white/10 bg-white/5 p-1 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={() => onLanguageChange("it")}
                  className={`px-4 py-3 min-h-[44px] text-xs font-body uppercase tracking-wider rounded-full transition-colors ${
                    lang === "it"
                      ? "bg-[#FF6B00] text-[#09101F] font-semibold"
                      : "text-white/70 hover:text-white"
                  }`}
                  data-testid="lang-btn-it"
                  aria-pressed={lang === "it"}
                >
                  Italiano
                </button>
                <button
                  type="button"
                  onClick={() => onLanguageChange("en")}
                  className={`px-4 py-3 min-h-[44px] text-xs font-body uppercase tracking-wider rounded-full transition-colors ${
                    lang === "en"
                      ? "bg-[#FF6B00] text-[#09101F] font-semibold"
                      : "text-white/70 hover:text-white"
                  }`}
                  data-testid="lang-btn-en"
                  aria-pressed={lang === "en"}
                >
                  English
                </button>
              </div>
            </div>
          )}

          {/* Title */}
          <h1
            className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-4"
            data-testid="legal-title"
          >
            {title}
          </h1>

          {/* Last Update */}
          <p
            className="font-body text-white/50 text-sm mb-12"
            data-testid="last-update"
          >
            {labels.lastUpdated}: {lastUpdate}
          </p>

          {/* Legal Content */}
          <div
            className="prose prose-invert prose-lg max-w-none font-body text-white/70 leading-relaxed"
            data-testid="legal-content"
          >
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="inline-block">
            <img
              src="/images/logo-192.png"
              alt="GozzoGuide Logo"
              className="h-8 w-auto opacity-50 hover:opacity-100 transition-opacity rounded-lg"
            />
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm">
            <Link to="/privacy" className="inline-block py-3 text-white/50 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="inline-block py-3 text-white/50 hover:text-white transition-colors">Terms</Link>
            <Link to="/cookie" className="inline-block py-3 text-white/50 hover:text-white transition-colors">Cookies</Link>
            <Link to="/app-privacy" className="inline-block py-3 text-white/50 hover:text-white transition-colors">App Privacy</Link>
            <Link to="/app-terms" className="inline-block py-3 text-white/50 hover:text-white transition-colors">App Terms</Link>
            <Link to="/app-cookie" className="inline-block py-3 text-white/50 hover:text-white transition-colors">App Cookies</Link>
            <Link to="/delete-account" className="inline-block py-3 text-white/50 hover:text-white transition-colors">Delete account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
